import type { FC } from "react";
import { Navigate, useParams } from "react-router-dom";

/**
 * Defines the inputs supplied to this component.
 */
interface ITrialConfigurationPageRedirectProps {
    destination: string;
    recordParameter?: string;
}

/**
 * Redirects the trial configuration route to its established destination.
 *
 * Preconditions: Render within the router that supplies the named route parameters.
 * Postconditions: Missing required parameters render nothing; otherwise Navigate requests a
 * history-replacing transition. Parameter strings are interpolated without numeric validation.
 * The redirect does not preserve the current query string or fragment.
 */
export const TrialConfigurationPageRedirect: FC<ITrialConfigurationPageRedirectProps> = ({
    destination,
    recordParameter
}) => {
    const params = useParams();
    const trialId = params.trialId;
    const recordId = recordParameter ? params[recordParameter] : undefined;

    if (!trialId || (recordParameter && !recordId)) {
        return null;
    }

    const recordPath = recordId ? `/${recordId}` : "";
    return (
        <Navigate
            to={`/trials/${trialId}/configuration/${destination}${recordPath}`}
            replace
        />
    );
};

/**
 * Preserves old configuration treatment URLs after Treatments moved to the trial section.
 *
 * Preconditions: The route supplies a trial ID and optionally a treatment ID or `create`.
 * Postconditions: Navigate replaces the old URL with the corresponding main-trial URL; a missing
 * trial ID renders nothing. The record segment is forwarded without validation.
 */
export const TrialTreatmentPageRedirect: FC = () => {
    const { trialId, trialTreatmentId } = useParams();

    if (!trialId) {
        return null;
    }

    const recordPath = trialTreatmentId ? `/${trialTreatmentId}` : "";
    return (
        <Navigate
            to={`/trials/${trialId}/treatments${recordPath}`}
            replace
        />
    );
};

/**
 * Defines the inputs supplied to this component.
 */
interface ITrialItemsPageRedirectProps {
    destination: string;
}

/**
 * Redirects the trial items route to its established destination.
 *
 * Preconditions: Render within the router that supplies the named route parameters.
 * Postconditions: Missing required parameters render nothing; otherwise Navigate requests a
 * history-replacing transition. Parameter strings are interpolated without numeric validation.
 * The redirect does not preserve the current query string or fragment.
 */
export const TrialItemsPageRedirect: FC<ITrialItemsPageRedirectProps> = ({ destination }) => {
    const { trialId } = useParams();

    if (!trialId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/items/${destination}`}
            replace
        />
    );
};

/**
 * Redirects the trial item route to its established destination.
 *
 * Preconditions: Render within the router that supplies the named route parameters.
 * Postconditions: Missing required parameters render nothing; otherwise Navigate requests a
 * history-replacing transition. Parameter strings are interpolated without numeric validation.
 * The redirect does not preserve the current query string or fragment.
 */
export const TrialItemPageRedirect: FC = () => {
    const { trialId, trialItemId, trialItemVersionId } = useParams();

    if (!trialId || !trialItemId) {
        return null;
    }

    const path = trialItemVersionId
        ? `${trialItemId}/versions/${trialItemVersionId}/entry`
        : `${trialItemId}`;

    return (
        <Navigate
            to={`/trials/${trialId}/items/trial-items/${path}`}
            replace
        />
    );
};

/**
 * Redirects the trial item entry route to its established destination.
 *
 * Preconditions: Render within the router that supplies the named route parameters.
 * Postconditions: Missing required parameters render nothing; otherwise Navigate requests a
 * history-replacing transition. Parameter strings are interpolated without numeric validation.
 * The redirect does not preserve the current query string or fragment.
 */
export const TrialItemEntryPageRedirect: FC = () => {
    const { trialId, trialItemId } = useParams();

    if (!trialId || !trialItemId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/items/trial-items/${trialItemId}/entry`}
            replace
        />
    );
};

/**
 * Redirects the trial institution details route to its established destination.
 *
 * Preconditions: Render within the router that supplies the named route parameters.
 * Postconditions: Missing required parameters render nothing; otherwise Navigate requests a
 * history-replacing transition. Parameter strings are interpolated without numeric validation.
 * The redirect does not preserve the current query string or fragment.
 */
export const TrialInstitutionDetailsPageRedirect: FC = () => {
    const { trialId, trialInstitutionId } = useParams();

    if (!trialId || !trialInstitutionId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/institutions/${trialInstitutionId}/details`}
            replace
        />
    );
};

/**
 * Redirects the trial institution participants route to its established destination.
 *
 * Preconditions: Render within the router that supplies the named route parameters.
 * Postconditions: Missing required parameters render nothing; otherwise Navigate requests a
 * history-replacing transition. Parameter strings are interpolated without numeric validation.
 * The redirect does not preserve the current query string or fragment.
 */
export const TrialInstitutionParticipantsPageRedirect: FC = () => {
    const { trialId, trialInstitutionId } = useParams();

    if (!trialId || !trialInstitutionId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/institutions/${trialInstitutionId}/participants`}
            replace
        />
    );
};

/**
 * Redirects the trial institution participant route to its established destination.
 *
 * Preconditions: Render within the router that supplies the named route parameters.
 * Postconditions: Missing required parameters render nothing; otherwise Navigate requests a
 * history-replacing transition. Parameter strings are interpolated without numeric validation.
 * The redirect does not preserve the current query string or fragment.
 */
export const TrialInstitutionParticipantPageRedirect: FC = () => {
    const { trialId, trialInstitutionId, patientId } = useParams();

    if (!trialId || !trialInstitutionId || !patientId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/institutions/${trialInstitutionId}/participants/${patientId}/details`}
            replace
        />
    );
};

/**
 * Redirects the trial participant route to its established destination.
 *
 * Preconditions: Render within the router that supplies the named route parameters.
 * Postconditions: Missing required parameters render nothing; otherwise Navigate requests a
 * history-replacing transition. Parameter strings are interpolated without numeric validation.
 * The redirect does not preserve the current query string or fragment.
 */
export const TrialParticipantPageRedirect: FC = () => {
    const { trialId, patientId } = useParams();

    if (!trialId || !patientId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/participants/${patientId}/details`}
            replace
        />
    );
};

/**
 * Redirects the institution department route to its established destination.
 *
 * Preconditions: Render within the router that supplies the named route parameters.
 * Postconditions: Missing required parameters render nothing; otherwise Navigate requests a
 * history-replacing transition. Parameter strings are interpolated without numeric validation.
 * The redirect does not preserve the current query string or fragment.
 */
export const InstitutionDepartmentPageRedirect: FC = () => {
    const { institutionId, departmentId } = useParams();

    if (!institutionId || !departmentId) {
        return null;
    }

    return (
        <Navigate
            to={`/institutions/${institutionId}/departments/${departmentId}/details`}
            replace
        />
    );
};

/**
 * Redirects the supporting site route to its details destination.
 *
 * Preconditions: Render within the router that supplies the named route parameters.
 * Postconditions: Missing required parameters render nothing; otherwise Navigate requests a
 * history-replacing transition. Parameter strings are interpolated without numeric validation.
 * The redirect does not preserve the current query string or fragment.
 */
export const TrialInstitutionSupportingSitePageRedirect: FC = () => {
    const { trialId, trialInstitutionId, supportingSiteId } = useParams();

    if (!trialId || !trialInstitutionId || !supportingSiteId) {
        return null;
    }

    return (
        <Navigate
            to={
                `/trials/${trialId}/institutions/${trialInstitutionId}` +
                `/supporting-sites/${supportingSiteId}/details`
            }
            replace
        />
    );
};

/**
 * Redirects the trial institution items route to its established destination.
 *
 * Preconditions: Render within the router that supplies the named route parameters.
 * Postconditions: Missing required parameters render nothing; otherwise Navigate requests a
 * history-replacing transition. Parameter strings are interpolated without numeric validation.
 * The redirect does not preserve the current query string or fragment.
 */
export const TrialInstitutionItemsPageRedirect: FC = () => {
    const { trialId, trialInstitutionId } = useParams();

    if (!trialId || !trialInstitutionId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/institutions/${trialInstitutionId}/items`}
            replace
        />
    );
};

/**
 * Redirects the trial institution item route to its established destination.
 *
 * Preconditions: Render within the router that supplies the named route parameters.
 * Postconditions: Missing required parameters render nothing; otherwise Navigate requests a
 * history-replacing transition. Parameter strings are interpolated without numeric validation.
 * The redirect does not preserve the current query string or fragment.
 */
export const TrialInstitutionItemPageRedirect: FC = () => {
    const { trialId, trialInstitutionId, trialInstitutionItemId } = useParams();

    if (!trialId || !trialInstitutionId || !trialInstitutionItemId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/institutions/${trialInstitutionId}/items/${trialInstitutionItemId}`}
            replace
        />
    );
};

/**
 * Redirects the trial institution personnel route to its established destination.
 *
 * Preconditions: Render within the router that supplies the named route parameters.
 * Postconditions: Missing required parameters render nothing; otherwise Navigate requests a
 * history-replacing transition. Parameter strings are interpolated without numeric validation.
 * The redirect does not preserve the current query string or fragment.
 */
export const TrialInstitutionPersonnelPageRedirect: FC = () => {
    const { trialId, trialInstitutionId } = useParams();

    if (!trialId || !trialInstitutionId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/institutions/${trialInstitutionId}/personnel`}
            replace
        />
    );
};

/**
 * Redirects the trial institution personnel details route to its established destination.
 *
 * Preconditions: Render within the router that supplies the named route parameters.
 * Postconditions: Missing required parameters render nothing; otherwise Navigate requests a
 * history-replacing transition. Parameter strings are interpolated without numeric validation.
 * The redirect does not preserve the current query string or fragment.
 */
export const TrialInstitutionPersonnelDetailsPageRedirect: FC = () => {
    const { trialId, trialInstitutionId, personnelRoleId } = useParams();

    if (!trialId || !trialInstitutionId || !personnelRoleId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/institutions/${trialInstitutionId}/personnel/${personnelRoleId}`}
            replace
        />
    );
};

/**
 * Redirects the trial institution notes route to its established destination.
 *
 * Preconditions: Render within the router that supplies the named route parameters.
 * Postconditions: Missing required parameters render nothing; otherwise Navigate requests a
 * history-replacing transition. Parameter strings are interpolated without numeric validation.
 * The redirect does not preserve the current query string or fragment.
 */
export const TrialInstitutionNotesPageRedirect: FC = () => {
    const { trialId, trialInstitutionId } = useParams();

    if (!trialId || !trialInstitutionId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/institutions/${trialInstitutionId}/notes`}
            replace
        />
    );
};

/**
 * Redirects the trial institution note route to its established destination.
 *
 * Preconditions: Render within the router that supplies the named route parameters.
 * Postconditions: Missing required parameters render nothing; otherwise Navigate requests a
 * history-replacing transition. Parameter strings are interpolated without numeric validation.
 * The redirect does not preserve the current query string or fragment.
 */
export const TrialInstitutionNotePageRedirect: FC = () => {
    const { trialId, trialInstitutionId, noteId } = useParams();

    if (!trialId || !trialInstitutionId || !noteId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/institutions/${trialInstitutionId}/notes/${noteId}`}
            replace
        />
    );
};

/**
 * Redirects the former group item template routes to the group configuration items routes.
 *
 * Postconditions: Navigate requests a history-replacing transition to the list, or to the template
 * when one is named. The redirect does not preserve the current query string or fragment.
 */
export const TrialGroupItemTemplateLegacyRedirect: FC = () => {
    const { groupId, trialGroupItemTemplateId } = useParams();

    return (
        <Navigate
            to={`/groups/${groupId}/configuration/items${
                trialGroupItemTemplateId ? `/${trialGroupItemTemplateId}` : ""
            }`}
            replace
        />
    );
};
